import { OpticalPortSortKey, ORIENTATIONS } from "../../DesignUploadStep.values";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Structure } from "@/types/designs";
import { DesignTableCell } from "../DesignTableCell";
import { useStructureTableSort } from "../../hooks/useStructureTableSort";
import { SortIcon } from "../icons/SortIcon";

type OpticalPortDraft = NonNullable<Structure["opticalPorts"]>[number];

type OpticalPortsTableProps = {
  isEditing?: boolean;
  draftPorts: OpticalPortDraft[];
  onDraftPortsChange: (draftPorts: OpticalPortDraft[]) => void;
};

export function OpticalPortsTable({
  isEditing = false,
  draftPorts,
  onDraftPortsChange,
}: OpticalPortsTableProps) {
  const { sortedRows, handleSort, sortState } = useStructureTableSort<OpticalPortSortKey>(
    draftPorts,
    { key: "name", direction: "asc" },
  );

  const updatePort = <K extends keyof OpticalPortDraft>(
    id: number,
    key: K,
    value: OpticalPortDraft[K],
  ) => {
    onDraftPortsChange(
      draftPorts.map((port) => (port.id === id ? { ...port, [key]: value } : port)),
    );
  };

  return (
    <div className="m-0">
      <Table className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[160px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("name")}
              >
                Name <SortIcon sortState={sortState} key="name" />
              </button>
            </TableHead>
            <TableHead className="w-[160px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("orientation")}
              >
                Orientation <SortIcon sortState={sortState} key="orientation" />
              </button>
            </TableHead>
            <TableHead className="w-[160px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("cellPort")}
              >
                Cell Port <SortIcon sortState={sortState} key="cellPort" />
              </button>
            </TableHead>
            <TableHead className="w-[180px]">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("tag")}
              >
                Tag <SortIcon sortState={sortState} key="tag" />
              </button>
            </TableHead>
            <TableHead className="w-[100px] text-right">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("x")}
              >
                X <SortIcon sortState={sortState} key="x" />
              </button>
            </TableHead>
            <TableHead className="w-[100px] text-right">
              <button
                type="button"
                className="inline-flex items-center gap-1"
                onClick={() => handleSort("y")}
              >
                Y <SortIcon sortState={sortState} key="y" />
              </button>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.length > 0 ? (
            sortedRows.map((port) => (
              <TableRow key={port.id}>
                <TableCell className="w-[160px]">
                  <DesignTableCell
                    value={port.name}
                    isEditing={isEditing}
                    onChange={(value) => updatePort(port.id, "name", String(value))}
                  />
                </TableCell>
                <TableCell className="w-[160px]">
                  <DesignTableCell
                    value={port.orientation}
                    isEditing={isEditing}
                    options={ORIENTATIONS}
                    inputType="dropdown"
                    onChange={(value) => updatePort(port.id, "orientation", String(value))}
                  />
                </TableCell>
                <TableCell className="w-[160px]">
                  <DesignTableCell
                    value={port.cellPort}
                    isEditing={isEditing}
                    onChange={(value) => updatePort(port.id, "cellPort", String(value))}
                  />
                </TableCell>
                <TableCell className="w-[180px]">
                  <DesignTableCell
                    value={port.tag}
                    isEditing={isEditing}
                    onChange={(value) => updatePort(port.id, "tag", String(value))}
                  />
                </TableCell>
                <TableCell className="w-[100px] text-right">
                  <DesignTableCell
                    value={port.x}
                    isEditing={isEditing}
                    inputType="number"
                    inputClassName="h-8 w-full text-right"
                    onChange={(value) => updatePort(port.id, "x", Number(value))}
                  />
                </TableCell>
                <TableCell className="w-[100px] text-right">
                  <DesignTableCell
                    value={port.y}
                    isEditing={isEditing}
                    inputType="number"
                    inputClassName="h-8 w-full text-right"
                    onChange={(value) => updatePort(port.id, "y", Number(value))}
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                No optical ports available.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
